import './advanced-spinner.styles.scss';
import React, { FC, useEffect, useState, useRef } from 'react';
import {Spinner, SpinProps} from '../ant-design/spinner';

/**
 * AdvancedSpinnerProps
 *
 * @memberof AdvancedSpinner
 */
interface AdvancedSpinnerProps {
    /**
     * Ant design spinner props
     */
    showSpinner: boolean,
    /**
     * Ant design spinner props
     */
    SpinProps?: SpinProps,
    /**
     * Should the loader cover entire screen or just child component.
     * 
     * Default: false
     */
    coverPage?: boolean,
    /**
     * Set a maximum timer for spinner to spin in milliseconds
     * 
     * Default: unlimited time
     */
    spinTimeDuration?: number,
    /**
     * Callback if the spinTimeDuration ran out and the spinner was turned off
     */
    callbackOnSpinnerTimeout?: () => void;
};



/**
 * Custom spinner that allows you to set a timeout and a cover full page
 */
export const AdvancedSpinner: FC<AdvancedSpinnerProps> = (props) => {
    const [isSpinning, setIsSpinning] = useState(true);
    const [spinTimeDuration, setSpinTimeDurations] =  useState<number | undefined>();
    const timer = useRef<ReturnType<typeof setTimeout>>();

    // when the spin time reaches it max time
    const onTimerComplete = () => {
        if(isSpinning){
            // if the spinner is spinning then we will stop it and call callback
            setIsSpinning(false);
            props.callbackOnSpinnerTimeout && props.callbackOnSpinnerTimeout();
        }
        timer.current = undefined;
    }

    useEffect(() => {

        // if the spinning state changes and a timer is active. Turn it off
        if(props.showSpinner !== isSpinning && timer.current){
            clearTimeout(timer.current);
        }

        // if the time duration changes stop the old timer and reset it with the new on if its valid
        if(props.spinTimeDuration !== spinTimeDuration) {
            if(timer.current){
                clearTimeout(timer.current);
            }
            if(props.spinTimeDuration){
                timer.current = setTimeout(onTimerComplete, props.spinTimeDuration);
            }
        }

        setSpinTimeDurations(props.spinTimeDuration);
        setIsSpinning(props.showSpinner);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    ,[props.showSpinner, props.spinTimeDuration])


    return (
        <div
            className={
                `${props.coverPage && props.showSpinner ? 'full-screen ' : '' }
                ${props.showSpinner ? 'custom-spinner ' : '' }`
            }
        >
            <Spinner
                spinning={isSpinning}
                {...props.SpinProps}
            >
                <div className={props.coverPage ? 'cover' : ''}>
                    {props.children}
                </div>
            </Spinner>
        </div>
    )
};
